import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { services } from '../mock';

const Header = ({ onOpenPopup, isLandingPage = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const handleChatClick = () => {
    // Open Tawktrik chat widget
    if (window.openChatWidget) {
      window.openChatWidget();
    }
  };

  // Close dropdown when clicking on a link
  const handleLinkClick = () => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  };

  if (isLandingPage) {
    return (
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex justify-between items-center h-16 md:h-20 gap-2 md:gap-4">
            <Link to="/lp" className="flex items-center flex-shrink-0">
              <img src="/assets/img/brand/logo-dark.png" alt="Trademark Inspire" className="h-10 md:h-14" />
            </Link>
            <div className="flex gap-2 md:gap-4">
              <button
                onClick={onOpenPopup}
                className="bg-[#093336] hover:bg-[#0a4548] text-white px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-2.5 rounded-lg text-[10px] sm:text-xs md:text-base font-medium transition-colors whitespace-nowrap"
              >
                Register My Trademark
              </button>
              <button
                onClick={handleChatClick}
                className="border-2 border-[#093336] text-[#093336] hover:bg-[#093336] hover:text-white px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-2.5 rounded-lg text-[10px] sm:text-xs md:text-base font-medium transition-colors whitespace-nowrap"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-white shadow-md sticky top-0 z-40">
      <div className="container mx-auto px-3 md:px-4">
        <div className="flex justify-between items-center h-16 md:h-20">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src="/assets/img/brand/logo-dark.png" alt="Trademark Inspire" className="h-10 md:h-14" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              onClick={handleLinkClick}
              className={`text-gray-700 hover:text-[#093336] font-medium transition-colors ${location.pathname === '/' ? 'text-[#093336]' : ''}`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              onClick={handleLinkClick}
              className={`text-gray-700 hover:text-[#093336] font-medium transition-colors ${location.pathname === '/about' ? 'text-[#093336]' : ''}`}
            >
              About Us
            </Link>
            
            {/* Services Mega Menu */}
            <div 
              className="relative group"
              onMouseEnter={() => setActiveDropdown('services')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center text-gray-700 hover:text-[#093336] font-medium transition-colors">
                Services <ChevronDown size={16} className="ml-1" />
              </button>
              
              {activeDropdown === 'services' && (
                <div 
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                  style={{ willChange: 'opacity' }}
                  onMouseEnter={() => setActiveDropdown('services')}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <div className="w-[1100px] bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-2xl border border-gray-200 overflow-hidden opacity-0 animate-fadeInSmooth">
                    <div className="bg-gradient-to-r from-[#093336] to-[#0a4548] px-6 py-2.5">
                      <h3 className="text-white font-semibold text-sm">Our Services</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4 p-5">
                      {services.map((serviceCategory, index) => (
                        <div 
                          key={index} 
                          className="bg-white rounded-lg p-3.5 border border-gray-100 hover:border-[#F77F00]/30 hover:shadow-md transition-all duration-300"
                        >
                          <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-gray-100">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#093336] to-[#0a4548] flex items-center justify-center">
                              <span className="w-2 h-2 bg-[#F77F00] rounded-full"></span>
                            </div>
                            <h4 className="font-bold text-[#093336] text-sm">
                              {serviceCategory.category}
                            </h4>
                          </div>
                          <ul className="space-y-1 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                            {serviceCategory.items.map((item, idx) => (
                              <li key={idx}>
                                <Link
                                  to={item.path}
                                  onClick={handleLinkClick}
                                  className="text-sm text-gray-600 hover:text-[#F77F00] hover:bg-gray-50 hover:pl-2 transition-all duration-200 block py-1.5 px-1 rounded group"
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="w-1 h-1 bg-gray-400 rounded-full group-hover:bg-[#F77F00] transition-colors"></span>
                                    {item.name}
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Link 
              to="/contact" 
              onClick={handleLinkClick}
              className={`text-gray-700 hover:text-[#093336] font-medium transition-colors ${location.pathname === '/contact' ? 'text-[#093336]' : ''}`}
            >
              Contact Us
            </Link>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={onOpenPopup}
              className="bg-[#093336] hover:bg-[#0a4548] text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
            >
              Get Started
            </button>
            <button
              onClick={handleChatClick}
              className="border-2 border-[#093336] text-[#093336] hover:bg-[#093336] hover:text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
            >
              Chat Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <Link 
              to="/" 
              onClick={handleLinkClick}
              className="block text-gray-700 hover:text-[#093336] font-medium py-2"
            >
              Home
            </Link>
            <Link 
              to="/about" 
              onClick={handleLinkClick}
              className="block text-gray-700 hover:text-[#093336] font-medium py-2"
            >
              About Us
            </Link>
            
            {/* Services Section in Mobile */}
            <div className="border-t border-gray-200 pt-3">
              <p className="text-sm font-bold text-[#093336] mb-2 px-2">Services</p>
              {services.map((serviceCategory, index) => (
                <div key={index} className="mb-3">
                  <p className="text-xs font-semibold text-gray-600 mb-1.5 px-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#F77F00] rounded-full"></span>
                    {serviceCategory.category}
                  </p>
                  <div className="space-y-1 pl-5">
                    {serviceCategory.items.map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.path}
                        onClick={handleLinkClick}
                        className="block text-sm text-gray-600 hover:text-[#F77F00] py-1.5"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <Link 
              to="/contact" 
              onClick={handleLinkClick}
              className="block text-gray-700 hover:text-[#093336] font-medium py-2 border-t border-gray-200 pt-3"
            >
              Contact Us
            </Link>
            <button
              onClick={onOpenPopup}
              className="w-full bg-[#093336] text-white px-6 py-2.5 rounded-lg font-medium mt-3"
            >
              Get Started
            </button>
            <button
              onClick={handleChatClick}
              className="w-full border-2 border-[#093336] text-[#093336] px-6 py-2.5 rounded-lg font-medium"
            >
              Chat Now
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
